import React from "react";
import { Check, ExternalLink } from "lucide-react";
import { Button, Field, Select, inputStyle } from "../UI";
import {
  JOB_BOARDS,
  boardComposerUrl,
  boardPastePlaceholder,
  boardInterestCopy,
  orderedSelectedBoards,
  boardLabel,
  jobInputStyle,
  FieldError
} from "./recruiterConstants";

export default function PostJobModal({
  isOpen,
  theme,
  fonts,
  jobPostStep,
  setJobPostStep,
  jobForm,
  setJobForm,
  jobFormErrors,
  setJobFormErrors,
  boardPublishQueue,
  boardPublishIndex,
  boardLinkJob,
  boardLinkUrl,
  setBoardLinkUrl,
  boardInterestAsk,
  busy,
  onJobFormSubmit,
  toggleJobPlatform,
  openOneBoardWindow,
  goToNextBoard,
  confirmBoardInterest,
  closeJobModal
}) {
  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000
    }}>
      <div style={{
        background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 14,
        width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column",
        position: "relative", overflow: "hidden"
      }}>
        <div style={{ padding: "24px 28px 16px", borderBottom: `1px solid ${theme.line}` }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 6px", fontFamily: fonts?.display }}>Post a Job</h3>
          <p style={{ fontSize: 13, color: theme.textMute, margin: 0 }}>
            Post here first. We syndicate across your chosen job boards step-by-step.
          </p>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {[
              { n: 1, label: "Job details" },
              { n: 2, label: "Choose boards" },
              { n: 3, label: "Post one by one" }
            ].map((s) => {
              const active = jobPostStep === s.n;
              const done = jobPostStep > s.n;
              return (
                <div key={s.n} style={{
                  flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                  borderRadius: 10, border: `1px solid ${active || done ? theme.accent : theme.line}`,
                  background: active ? theme.accent : (done ? `${theme.accent}14` : "transparent"),
                  color: active ? "#fff" : theme.text
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, background: active ? "rgba(255,255,255,0.2)" : theme.accentDim, color: active ? "#fff" : theme.accent
                  }}>
                    {done ? <Check size={13} /> : s.n}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={onJobFormSubmit} style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <div style={{ padding: "20px 28px", overflowY: "auto", flex: 1 }}>
            {jobPostStep === 1 ? (
              <>
                <Field label="Job title" theme={theme}>
                  <input type="text" style={jobInputStyle(theme, jobFormErrors.role)} value={jobForm.role} onChange={e => { setJobForm({ ...jobForm, role: e.target.value }); setJobFormErrors((p) => ({ ...p, role: undefined })); }} placeholder="e.g. Full Stack Developer" />
                  <FieldError message={jobFormErrors.role} />
                </Field>
                <Field label="Job description" theme={theme}>
                  <textarea style={{ ...jobInputStyle(theme, jobFormErrors.description), minHeight: 110, resize: "vertical" }} value={jobForm.description} onChange={e => { setJobForm({ ...jobForm, description: e.target.value }); setJobFormErrors((p) => ({ ...p, description: undefined })); }} placeholder="Role summary, responsibilities, and must-have skills" />
                  <div style={{ fontSize: 11, color: theme.textMute, marginTop: 4 }}>{String(jobForm.description || "").trim().length}/40 characters minimum</div>
                  <FieldError message={jobFormErrors.description} />
                </Field>
                <Field label="Key skills (comma-separated)" theme={theme}>
                  <input type="text" style={jobInputStyle(theme, jobFormErrors.skills)} value={jobForm.skills} onChange={e => { setJobForm({ ...jobForm, skills: e.target.value }); setJobFormErrors((p) => ({ ...p, skills: undefined })); }} placeholder="e.g. C#, .NET Core, React, SQL" />
                  <FieldError message={jobFormErrors.skills} />
                </Field>
                <div className="row g-2">
                  <div className="col-md-6">
                    <Field label="Experience required" theme={theme}>
                      <input type="text" style={jobInputStyle(theme, jobFormErrors.experience)} value={jobForm.experience} onChange={e => { setJobForm({ ...jobForm, experience: e.target.value }); setJobFormErrors((p) => ({ ...p, experience: undefined })); }} placeholder="e.g. 2-5 years" />
                      <FieldError message={jobFormErrors.experience} />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Salary range" theme={theme}>
                      <input type="text" style={jobInputStyle(theme, jobFormErrors.salary_range)} value={jobForm.salary_range} onChange={e => { setJobForm({ ...jobForm, salary_range: e.target.value }); setJobFormErrors((p) => ({ ...p, salary_range: undefined })); }} placeholder="e.g. Rs. 8,00,000 - 12,00,000" />
                      <FieldError message={jobFormErrors.salary_range} />
                    </Field>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <Field label="Location" theme={theme}>
                      <input type="text" style={jobInputStyle(theme, jobFormErrors.location)} value={jobForm.location} onChange={e => { setJobForm({ ...jobForm, location: e.target.value }); setJobFormErrors((p) => ({ ...p, location: undefined })); }} placeholder="e.g. Chennai, Tamil Nadu" />
                      <FieldError message={jobFormErrors.location} />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Employment type" theme={theme}>
                      <Select
                        theme={theme}
                        value={jobForm.employment_type}
                        onChange={e => setJobForm({ ...jobForm, employment_type: e.target.value })}
                        options={[
                          { value: "Full-time", label: "Full-time" },
                          { value: "Part-time", label: "Part-time" },
                          { value: "Contract", label: "Contract" },
                          { value: "Internship", label: "Internship" }
                        ]}
                      />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Openings" theme={theme}>
                      <input type="number" min="1" style={jobInputStyle(theme, jobFormErrors.openings)} value={jobForm.openings} onChange={e => { setJobForm({ ...jobForm, openings: e.target.value }); setJobFormErrors((p) => ({ ...p, openings: undefined })); }} />
                      <FieldError message={jobFormErrors.openings} />
                    </Field>
                  </div>
                </div>
              </>
            ) : jobPostStep === 2 ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Where should we post this job?</div>
                <p style={{ fontSize: 12, color: theme.textMute, marginBottom: 14 }}>
                  LinkedIn is first, then Indeed. For Foundit (Formdin), Naukri, or Glassdoor we first ask if you are interested.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {JOB_BOARDS.map((board) => {
                    const checked = jobForm.platforms.includes(board.id);
                    return (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => toggleJobPlatform(board.id)}
                        style={{
                          textAlign: "left",
                          padding: "14px 16px",
                          borderRadius: 12,
                          border: `2px solid ${checked ? board.color : theme.line}`,
                          background: checked ? `${board.color}12` : theme.bg,
                          cursor: "pointer",
                          display: "flex",
                          gap: 12,
                          alignItems: "flex-start"
                        }}
                      >
                        <span style={{
                          width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                          border: `2px solid ${checked ? board.color : theme.line}`,
                          background: checked ? board.color : "transparent",
                          color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center"
                        }}>
                          {checked ? <Check size={14} /> : null}
                        </span>
                        <span>
                          <span style={{ display: "block", fontWeight: 800, fontSize: 14, color: checked ? board.color : theme.text }}>
                            {board.label}
                            {board.primary ? (
                              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: `${board.color}22`, color: board.color }}>Recommended</span>
                            ) : (
                              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: theme.accentDim, color: theme.textMute }}>Ask first</span>
                            )}
                          </span>
                          <span style={{ display: "block", fontSize: 12, color: theme.textMute, marginTop: 4 }}>{board.blurb}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <FieldError message={jobFormErrors.platforms} />
              </>
            ) : (
              <>
                {(() => {
                  const currentId = boardPublishQueue[boardPublishIndex] || "linkedin";
                  const current = JOB_BOARDS.find((b) => b.id === currentId) || { id: currentId, label: boardLabel(currentId), color: theme.accent };
                  const nextId = boardPublishQueue[boardPublishIndex + 1];
                  const nextName = nextId ? boardLabel(nextId) : "";
                  const stepN = boardPublishIndex + 1;
                  const total = Math.max(1, boardPublishQueue.length);
                  return (
                    <div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                        {boardPublishQueue.map((id, i) => {
                          const board = JOB_BOARDS.find((b) => b.id === id);
                          const active = i === boardPublishIndex;
                          const done = i < boardPublishIndex;
                          return (
                            <span key={id} style={{
                              fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 99,
                              background: active ? (board?.color || theme.accent) : (done ? `${board?.color || theme.accent}22` : theme.bg),
                              color: active ? "#fff" : (board?.color || theme.text),
                              border: `1px solid ${board?.color || theme.line}`
                            }}>
                              {done ? "OK " : `${i + 1}. `}{board?.label || id}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{
                        padding: 18, borderRadius: 14,
                        border: `2px solid ${current.color || theme.accent}`,
                        background: `${current.color || theme.accent}10`
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: current.color }}>
                          {stepN} of {total}
                        </div>
                        <h4 style={{ fontSize: 20, fontWeight: 800, margin: "6px 0 8px" }}>Post on {current.label}</h4>
                        <p style={{ fontSize: 13, color: theme.textMute, margin: 0, lineHeight: 1.5 }}>
                          Job details are copied. Open {current.label}, paste the job, and publish. Then come back here
                          {nextName ? ` and continue to ${nextName}` : " to finish"}. Only one board is open at a time.
                        </p>
                        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 12 }}>{jobForm.role || boardLinkJob?.role}</div>
                        <Button
                          type="button"
                          theme={theme}
                          onClick={() => openOneBoardWindow(boardLinkJob || jobForm, currentId)}
                          style={{ marginTop: 14, background: current.color, border: "none", justifyContent: "center" }}
                        >
                          <ExternalLink size={14} /> Open {current.label} job post
                        </Button>
                      </div>
                      <Field label={`Paste live ${current.label} job URL (optional)`} theme={theme}>
                        <input
                          type="url"
                          style={inputStyle(theme)}
                          value={boardLinkUrl}
                          onChange={(e) => setBoardLinkUrl(e.target.value)}
                          placeholder={boardPastePlaceholder(currentId)}
                        />
                      </Field>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <div style={{ padding: "14px 28px 20px", borderTop: `1px solid ${theme.line}`, display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {jobPostStep === 1 && (
              <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={closeJobModal} style={{ background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                Cancel
              </Button>
            )}
            {jobPostStep === 2 && (
              <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={() => setJobPostStep(1)} style={{ background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                Back
              </Button>
            )}
            {jobPostStep === 3 && (
              <>
                <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={() => goToNextBoard({ skip: true })} style={{ background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                  Skip {boardLabel(boardPublishQueue[boardPublishIndex] || "")}
                </Button>
                <Button type="button" theme={theme} disabled={busy} onClick={() => goToNextBoard({ skip: false })} style={{ minWidth: 200, justifyContent: "center" }}>
                  {boardPublishIndex + 1 >= boardPublishQueue.length
                    ? "Finish"
                    : `Continue to ${boardLabel(boardPublishQueue[boardPublishIndex + 1] || "")}`}
                </Button>
              </>
            )}
            {jobPostStep < 3 && (
              <Button type="submit" theme={theme} disabled={busy} style={{ minWidth: 220, justifyContent: "center" }}>
                {jobPostStep === 1
                  ? "Continue"
                  : (busy ? "Saving job..." : `Start with ${boardLabel(orderedSelectedBoards(jobForm.platforms)[0] || "linkedin")}`)}
              </Button>
            )}
          </div>
        </form>
      </div>

      {boardInterestAsk && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 2, background: "rgba(15,23,42,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20
        }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 440, boxShadow: "0 18px 40px rgba(15,23,42,0.2)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: boardInterestAsk.color, marginBottom: 8 }}>Optional job board</div>
            <h4 style={{ fontSize: 18, fontWeight: 800, margin: "0 0 10px" }}>{boardInterestCopy(boardInterestAsk).title}</h4>
            <p style={{ fontSize: 13, color: theme.textMute, margin: "0 0 18px", lineHeight: 1.5 }}>{boardInterestCopy(boardInterestAsk).body}</p>
            <div style={{ display: "flex", gap: 10 }}>
              <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={() => confirmBoardInterest(false)} style={{ flex: 1, justifyContent: "center", background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                Not interested
              </Button>
              <Button type="button" theme={theme} onClick={() => confirmBoardInterest(true)} style={{ flex: 1, justifyContent: "center", background: boardInterestAsk.color, border: "none" }}>
                Yes, I am interested
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
